import React, { useCallback } from 'react'
import { Box, Stack, useTheme, Skeleton, Tooltip } from '@mui/material'
import moment from 'moment/moment'
import { useTranslation } from 'react-i18next'
import EmptyScreen from 'packages/ui/EmptyScreen'

import { CircleInfoIcon } from 'components/global/CUK/Icons'

const HR = () => {
  const theme = useTheme()
  return (
    <Box sx={{ height: '1px', width: '100%', backgroundColor: theme.palette.grey[100] }} mt={1} mb={1} />
  )
}

interface ItemProps {
  title: string
  tooltip?: string
  value: React.ReactNode,
  hr?: boolean
}

const Item:React.FC<ItemProps> = ({title, tooltip = null, value, hr = true}) => {
  const theme = useTheme()
  return (
    <Stack>
      <Stack justifyContent={'space-between'} flex={1} flexDirection={'row'}>
        <Stack
          sx={{color: theme.palette.grey['800'], fontSize: 14}}
          direction={'row'}
          alignItems={'center'}
          gap={.5}
        >
          {title}
          {Boolean(tooltip) && (
            <Tooltip title={tooltip}>
              <Box><CircleInfoIcon /></Box>
            </Tooltip>
          )}
        </Stack>
        <Box sx={{color: theme.palette.grey['800'], fontSize: 14}}>{value}</Box>
      </Stack>
      {hr && <HR />}
    </Stack>
  )
}

const ReportCDRs = ({loading, stats}) => {
  const theme = useTheme()
  const {t} = useTranslation()
  
  const duration = useCallback((value) => {
    if (!value || isNaN(value)) return '-'
    if (value > 60) {
      const minutes = moment().startOf('day').add(value, 'seconds').format('m')
      const seconds = moment().startOf('day').add(value, 'seconds').format('s')
      return t('voip.report_cdr.average_duration_in_minute', {minutes, seconds})
    } else {
      return t('voip.report_cdr.average_duration_in_second', {count: value})
    }
  }, [t])

  const hasData = stats && (stats.count_call_success > 0 || stats.count_call_failure > 0)

  return (
    <Stack 
      sx={{ 
        border: hasData ? `1px solid ${theme.palette.grey[200]}` : '1px dashed #D7DAEA',
        borderRadius: '24px',
        minHeight: hasData ? 'auto' : 'calc(100vh - 446px)',
        display: 'flex',
        alignItems: hasData ? 'stretch' : 'center',
        justifyContent: hasData ? 'flex-start' : 'center'
      }} 
      p={2}
    >
      {loading ? (
        <Skeleton variant={'rectangular'} width={'100%'} height={206} sx={{ borderRadius: '24px' }} />
      ) : !hasData ? (
        <EmptyScreen icon={'phone-volume'} variant="compact">
          {t('voip.report_cdr.no_data')}
        </EmptyScreen>
      ) : (
        <>
          <Item
            title={t('voip.report_cdr.answered_call')}
            value={
              <Stack direction={'row'} spacing={.5}>
                <Box>{stats.count_call_success || 0}</Box>
                <Box>/ {(stats.count_call_success || 0) + (stats.count_call_failure || 0)}</Box>
              </Stack>
            }
          />
          <Item 
            title={t('voip.report_cdr.quality_of_service')} 
            value={`${Math.round(stats.percent_call_success || 0)} %`} 
            tooltip={t('voip.report_cdr.quality_of_service_tooltip')}
          />
          <Item title={t('voip.report_cdr.quality_queue')} value={`${Math.round(stats.percent_call_failure || 0)} %`} />
          <Item title={t('voip.report_cdr.tranfser_queue')} value={`${Math.round(stats.percent_transfer || 0)} %`} />
          <Item title={t('voip.report_cdr.outgoing_call')} value={stats.count_outgoing_call || 0} />
          <Item title={t('voip.report_cdr.waiting_duration')} value={duration(stats.average_waiting_duration)} />
          <Item hr={false} title={t('voip.report_cdr.call_duration')} value={duration(stats.average_call_duration)} />
        </>
      )}
    </Stack>
  )
}

export default ReportCDRs